/**
 * GR “Link existing invoice” — library candidates for a receipted PO.
 * Reuses Invoices Match gates / scoring fields (no second engine).
 *
 * Status story (keep tooltips / toasts aligned):
 * - Receipted = delivery saved on GR (not the same as reconciled)
 * - Soft-link / Invoice attached = on the PO, Match & reconcile not finished
 * - Reconciled = Match & reconcile finished (or Sent to Xero)
 */

import {
  canShowMatchAction,
  getInvoiceMatchActionMeta,
  MATCH_ACTION_PRIMARY,
} from './invoiceMatchActionHelpers'
import { formatInvoiceTotalDisplay } from './invoiceUploadHelpers'
import { isCreditNoteDocument } from './creditNoteDocumentHelpers'

const TERMINAL_LIFECYCLES = new Set(['reconciled', 'sentToXero'])

/** Shared GR Add invoice / Link existing copy (Chunk 5 + L2 discoverability). */
export const GR_LINK_EXISTING_COPY = {
  chooserLead:
    'Start with Link existing when the invoice is already in Invoices & Credit Notes — that avoids a duplicate upload. Use Upload new only if it is not in the library yet.',
  linkExistingOptionTitle: 'Link existing invoice (recommended)',
  linkExistingOptionHint:
    'Pick an open invoice already uploaded for this supplier — fastest when the file is already in OMS',
  uploadNewOptionTitle: 'Upload new invoice',
  uploadNewOptionHint:
    'Only if this file is not in the library yet — same upload & OCR as Invoices & Credit Notes',
  linkOnlyTooltip:
    'Attach this invoice to the PO without reconciling (soft-link). Receipted delivery stays as-is — this does not mark the invoice Reconciled.',
  linkAndReconcileTooltip:
    'Attach and Match & reconcile in one step (within tolerance). That finishes reconciliation; Receipted is still only delivery truth.',
  linkOnlySuccess:
    'Status is Invoice attached — not Reconciled yet. Finish with Match & reconcile when totals align.',
  outsideToleranceReason:
    'Totals are not within tolerance for this PO — use Link only, then finish Match & reconcile when ready.',
  possibleDuplicateReason:
    'Possible duplicate — review on Invoices & Credit Notes before reconciling.',
  emptyLibrary:
    'No open invoices or credit notes for this supplier in the library. Upload a new file, or check Invoices & Credit Notes.',
  emptySuggestions:
    'No strong match for this PO yet — browse or search below for another open invoice or credit note, or upload a new file.',
  emptyBrowse: 'All open documents for this supplier are in Suggested above.',
  emptySearch: 'No documents match that search.',
  applyCreditOnlyTooltip:
    'Apply this credit note to the PO. It reduces what you still need to match — it does not reconcile on its own.',
  applyCreditSuccess:
    'Credit applied. Status is Applied — not Reconciled. Match & reconcile an invoice when the remaining balance is zero.',
  creditNoteReconcileDisabled:
    'Credit notes are applied to the PO — they do not Match & reconcile alone.',
}

function normalisePo(value) {
  return String(value || '').trim()
}

function normaliseId(value) {
  return String(value || '').trim()
}

function invoiceIdKey(invoice = {}) {
  return String(invoice.id || invoice._id || '')
}

/** @deprecated Prefer isCreditNoteDocument from creditNoteDocumentHelpers (Chunk C0). */
export function isCreditNoteInvoice(invoice = {}) {
  return isCreditNoteDocument(invoice)
}

export function invoiceMatchesGrSupplier(invoice = {}, order = {}) {
  const orderSupplierId = normaliseId(order?.supplier?._id || order?.supplier)
  const invoiceSupplierId = normaliseId(invoice.supplierId || invoice.supplier)
  if (orderSupplierId && invoiceSupplierId) {
    return (
      orderSupplierId === invoiceSupplierId ||
      normaliseId(order.venueSupplier?._id) === invoiceSupplierId
    )
  }

  const orderTitle = String(order?.supplier?.title || '')
    .trim()
    .toLowerCase()
  const invoiceTitle = String(invoice.supplierTitle || '')
    .trim()
    .toLowerCase()
  if (orderTitle && invoiceTitle) {
    return orderTitle === invoiceTitle
  }
  return false
}

/**
 * Whether this invoice is eligible for Link existing on GR (invoices only).
 */
export function isLinkExistingCandidate(invoice = {}) {
  if (!invoice) return false
  if (isCreditNoteInvoice(invoice)) return false
  if (TERMINAL_LIFECYCLES.has(invoice.status || '')) return false
  if (invoice.isArchived) return false
  if (!canShowMatchAction(invoice)) return false
  return true
}

/**
 * Chunk C3 — open credit notes eligible to Apply to this GR PO.
 */
export function isLinkExistingCreditNoteCandidate(invoice = {}) {
  if (!invoice) return false
  if (!isCreditNoteDocument(invoice)) return false
  if (TERMINAL_LIFECYCLES.has(invoice.status || '')) return false
  if (invoice.isArchived) return false
  if (!canShowMatchAction(invoice)) return false
  return true
}

function collectOrderLineIds(order = {}) {
  const ids = []
  if (order?._id) ids.push(String(order._id))
  if (Array.isArray(order?.orderIds)) {
    order.orderIds.forEach(id => {
      if (id) ids.push(String(id))
    })
  }
  if (Array.isArray(order?.orders)) {
    order.orders.forEach(line => {
      if (line?._id) ids.push(String(line._id))
      if (line?.order) ids.push(String(line.order))
      if (line?.orderId) ids.push(String(line.orderId))
    })
  }
  return [...new Set(ids.filter(Boolean))]
}

/**
 * Same touch rules as GR Match (PO / allocations / matched ids).
 */
export function invoiceTouchesGrPurchaseOrder(invoice = {}, order = {}) {
  const po = normalisePo(order.purchaseOrderNumber)
  const orderIds = collectOrderLineIds(order)
  if (!po && !orderIds.length) return false

  if (po && normalisePo(invoice.purchaseOrderNumber) === po) return true

  const matchedPos = Array.isArray(invoice.matchedPurchaseOrderNumbers)
    ? invoice.matchedPurchaseOrderNumbers.map(normalisePo).filter(Boolean)
    : []
  if (po && matchedPos.includes(po)) return true

  const allocatedPos = Array.isArray(invoice.poAllocations)
    ? invoice.poAllocations
        .map(row => normalisePo(row?.purchaseOrderNumber))
        .filter(Boolean)
    : []
  if (po && allocatedPos.includes(po)) return true

  const summaryPo = normalisePo(invoice.matchSummary?.purchaseOrderNumber)
  if (po && summaryPo === po) return true

  const linkedId = invoice.linkedOrderId ? String(invoice.linkedOrderId) : ''
  if (linkedId && orderIds.includes(linkedId)) return true

  const matchedIds = Array.isArray(invoice.matchedOrderIds)
    ? invoice.matchedOrderIds.map(id => String(id))
    : []
  if (matchedIds.some(id => orderIds.includes(id))) return true

  const allocatedOrderIds = Array.isArray(invoice.poAllocations)
    ? invoice.poAllocations
        .map(row => (row?.orderId ? String(row.orderId) : ''))
        .filter(Boolean)
    : []
  if (allocatedOrderIds.some(id => orderIds.includes(id))) return true

  return false
}

/**
 * Higher = stronger smart-match signal for this PO.
 */
export function getLinkExistingSuggestionScore(invoice = {}, order = {}) {
  const po = normalisePo(order.purchaseOrderNumber)
  let score = 0

  if (invoiceTouchesGrPurchaseOrder(invoice, order)) score += 100

  const meta = getInvoiceMatchActionMeta(invoice)
  if (meta.primaryAction === MATCH_ACTION_PRIMARY.MATCH_AND_RECONCILE) {
    score += 50
  } else if (invoice.matchingStatus === 'match_found') {
    score += 40
  } else if (invoice.matchingStatus === 'partial_match') {
    score += 30
  } else if (invoice.matchingStatus === 'needs_action') {
    score += 10
  }

  if (po && normalisePo(invoice.matchSummary?.purchaseOrderNumber) === po) {
    score += 20
  }
  if (po && normalisePo(invoice.purchaseOrderNumber) === po) {
    score += 15
  }

  return score
}

export function isLinkExistingSuggestion(invoice = {}, order = {}) {
  if (getLinkExistingSuggestionScore(invoice, order) >= 100) return true
  const po = normalisePo(order.purchaseOrderNumber)
  if (!po) return false
  if (normalisePo(invoice.matchSummary?.purchaseOrderNumber) === po) return true
  const matchedPos = Array.isArray(invoice.matchedPurchaseOrderNumbers)
    ? invoice.matchedPurchaseOrderNumbers.map(normalisePo)
    : []
  return matchedPos.includes(po)
}

/**
 * Open invoices + credit notes for this GR supplier (C3 includes Apply CN).
 */
export function getLinkExistingCandidatesForOrder(
  order = {},
  invoiceRows = [],
) {
  return (invoiceRows || []).filter(invoice => {
    if (!invoiceMatchesGrSupplier(invoice, order)) return false
    return (
      isLinkExistingCandidate(invoice) ||
      isLinkExistingCreditNoteCandidate(invoice)
    )
  })
}

function compareSuggestionRank(a, b, order) {
  const scoreDiff =
    getLinkExistingSuggestionScore(b, order) -
    getLinkExistingSuggestionScore(a, order)
  if (scoreDiff !== 0) return scoreDiff
  const aDate = new Date(a.uploadedDate || a.invoiceDate || 0).getTime()
  const bDate = new Date(b.uploadedDate || b.invoiceDate || 0).getTime()
  return bDate - aDate
}

export function filterLinkExistingBySearchQuery(invoices = [], query = '') {
  const q = String(query || '')
    .trim()
    .toLowerCase()
  if (!q) return invoices

  return invoices.filter(invoice => {
    const haystack = [
      invoice.invoiceNumber,
      invoice.fileName,
      invoice.originalFilename,
      invoice.purchaseOrderNumber,
      invoice.supplierTitle,
      invoice.invoiceDate ? String(invoice.invoiceDate).slice(0, 10) : '',
      formatInvoiceTotalDisplay(invoice.invoiceTotal),
    ]
      .map(value => String(value || '').toLowerCase())
      .join(' ')
    return haystack.includes(q)
  })
}

/**
 * Split library into Suggested (for this PO) + Browse (rest / all searchable).
 *
 * @returns {{
 *   candidates: object[],
 *   suggested: object[],
 *   browse: object[],
 *   filtered: object[],
 * }}
 */
export function getLinkExistingListsForOrder(
  order = {},
  invoiceRows = [],
  searchQuery = '',
) {
  const candidates = getLinkExistingCandidatesForOrder(order, invoiceRows)
  const suggested = candidates
    .filter(invoice => isLinkExistingSuggestion(invoice, order))
    .sort((a, b) => compareSuggestionRank(a, b, order))

  const suggestedIds = new Set(suggested.map(invoiceIdKey))
  const browse = candidates
    .filter(invoice => !suggestedIds.has(invoiceIdKey(invoice)))
    .sort((a, b) => compareSuggestionRank(a, b, order))

  const filtered = filterLinkExistingBySearchQuery(
    candidates,
    searchQuery,
  ).sort((a, b) => compareSuggestionRank(a, b, order))

  return {
    candidates,
    suggested,
    browse,
    filtered,
  }
}

export function getLinkExistingInvoiceId(invoice = {}) {
  return invoiceIdKey(invoice)
}

/**
 * Whether Link & reconcile should be offered for the selection (Chunk 3).
 */
export function canLinkAndReconcileSelection(invoice = {}) {
  if (!invoice) return false
  if (isCreditNoteDocument(invoice)) return false
  if (invoice.matchingStatus === 'possible_duplicate') return false
  const meta = getInvoiceMatchActionMeta(invoice)
  return meta.primaryAction === MATCH_ACTION_PRIMARY.MATCH_AND_RECONCILE
}

/**
 * Confirm-action state for the selected library invoice or credit note.
 */
export function getLinkExistingConfirmState(invoice = null) {
  if (!invoice) {
    return {
      canLink: false,
      canReconcile: false,
      isCreditNote: false,
      reconcileDisabledReason: 'Select an invoice first.',
      isPossibleDuplicate: false,
    }
  }

  const isCreditNote = isCreditNoteDocument(invoice)
  const isPossibleDuplicate = invoice.matchingStatus === 'possible_duplicate'
  const canReconcile = canLinkAndReconcileSelection(invoice)

  let reconcileDisabledReason = null
  if (isCreditNote) {
    reconcileDisabledReason = GR_LINK_EXISTING_COPY.creditNoteReconcileDisabled
  } else if (isPossibleDuplicate) {
    reconcileDisabledReason = GR_LINK_EXISTING_COPY.possibleDuplicateReason
  } else if (!canReconcile) {
    reconcileDisabledReason = GR_LINK_EXISTING_COPY.outsideToleranceReason
  }

  return {
    canLink: true,
    canReconcile,
    isCreditNote,
    reconcileDisabledReason,
    isPossibleDuplicate,
  }
}

/**
 * Empty / hint state for the Link existing list UI.
 *
 * @returns {{
 *   kind: 'empty_library' | 'empty_search' | 'empty_suggestions' | 'ready',
 *   message: string,
 *   showUploadCta: boolean,
 * }}
 */
export function getLinkExistingEmptyState({
  candidates = [],
  suggested = [],
  browse = [],
  filtered = [],
  searchQuery = '',
} = {}) {
  const queryActive = Boolean(String(searchQuery || '').trim())

  if (!candidates.length) {
    return {
      kind: 'empty_library',
      message: GR_LINK_EXISTING_COPY.emptyLibrary,
      showUploadCta: true,
    }
  }

  if (queryActive && !filtered.length) {
    return {
      kind: 'empty_search',
      message: GR_LINK_EXISTING_COPY.emptySearch,
      showUploadCta: true,
    }
  }

  if (!queryActive && !suggested.length) {
    return {
      kind: 'empty_suggestions',
      message: GR_LINK_EXISTING_COPY.emptySuggestions,
      showUploadCta: browse.length === 0,
    }
  }

  return {
    kind: 'ready',
    message: '',
    showUploadCta: false,
  }
}

/**
 * Link body for attaching the picked invoice to this GR PO.
 */
export function buildGrLinkExistingBody(order = {}, invoice = {}) {
  const po = normalisePo(order.purchaseOrderNumber)
  const orderIds = collectOrderLineIds(order)
  const linkedOrderId = orderIds[0] || undefined

  const recommendedPo = normalisePo(
    invoice?.matchSummary?.purchaseOrderNumber ||
      (Array.isArray(invoice?.matchedPurchaseOrderNumbers)
        ? invoice.matchedPurchaseOrderNumbers[0]
        : ''),
  )
  const recommendsThisPo = Boolean(po && recommendedPo && recommendedPo === po)

  if (recommendsThisPo) {
    return {
      useRecommended: true,
      purchaseOrderNumber: po,
      linkedOrderId: linkedOrderId || undefined,
    }
  }

  return {
    purchaseOrderNumber: po,
    linkedOrderId: linkedOrderId || undefined,
  }
}
